import type { ReactNode } from "react";
import { LEGAL_PATHS } from "./paths.js";
import { LEGAL_LAST_UPDATED, formatLegalDate, supportHref, useSite } from "./site.js";

/**
 * The frame every legal page shares (GH-812): the title, the date, and the
 * entity the page speaks for.

 */
export function LegalShell({
  title,
  children,
}: {
  readonly title: string;
  readonly children: ReactNode;
}) {
  const site = useSite();
  return (
    <main className="card legal">
      <p className="legal__home">
        <a href="/">{site.productName}</a>
      </p>
      <h1 className="screen-title">{title}</h1>
      <p className="muted small legal__updated">
        Last updated {formatLegalDate(LEGAL_LAST_UPDATED)} · {site.productName} is operated by{" "}
        {site.organizationLegalName}
      </p>
      {children}
    </main>
  );
}

/**
 * A decision not yet made, written so nobody can mistake it for one that was.
 *
 * Square brackets on screen and a distinct style, so a reader sees a gap rather than a promise.
 * Every one of these is listed in `docs/evidence/GH-812.md` for counsel or the owner to fill.
 */
export function Pending({ children }: { readonly children: ReactNode }) {
  return <span className="legal__pending">[{children}]</span>;
}

/** The configured support contact as a link, or a visible gap where none is configured. */
export function SupportContact() {
  const { supportContact } = useSite();
  if (!supportContact) return <Pending>support contact not yet configured</Pending>;
  const href = supportHref(supportContact);
  return href ? <a href={href}>{supportContact}</a> : <span>{supportContact}</span>;
}

/** An in-page section with an anchor, so a link can land on it (`/terms#refunds`). */
export function LegalSection({
  id,
  title,
  children,
}: {
  readonly id: string;
  readonly title: string;
  readonly children: ReactNode;
}) {
  return (
    <section className="legal__section" id={id} aria-labelledby={`${id}-title`}>
      <h2 id={`${id}-title`} className="legal__heading">
        {title}
      </h2>
      {children}
    </section>
  );
}

/** A link to another legal page by key, so a renamed path is renamed everywhere. */
export function LegalLink({
  to,
  hash,
  children,
}: {
  readonly to: keyof typeof LEGAL_PATHS;
  readonly hash?: string;
  readonly children: ReactNode;
}) {
  return <a href={`${LEGAL_PATHS[to]}${hash ? `#${hash}` : ""}`}>{children}</a>;
}
